import { Loader2 } from "lucide-react";
import type { ReactElement } from "react";
import { cn } from "../ui/cn";

/**
 * @file LoadingSpinner.tsx
 * @description Reusable loading spinner component using shadcn/ui Loader2 icon
 *
 * Features the classic circular spinner with smooth rotation animation.
 * Uses Lucide's Loader2 icon styled with Lumi accent color.
 * Automatically centers in parent if `centered` prop is true.
 */

interface LoadingSpinnerProps {
  /** Size variant - defaults to medium */
  readonly size?: "sm" | "md" | "lg";
  /** Center the spinner in a full-screen container */
  readonly centered?: boolean;
  /** Additional CSS classes for custom styling */
  readonly className?: string;
}

const sizeConfig = {
  sm: 16,
  md: 24,
  lg: 32,
} as const;

/**
 * Loading spinner component using Loader2 icon
 *
 * @example
 * ```tsx
 * // Full screen centered
 * <LoadingSpinner centered />
 *
 * // Inline small spinner
 * <LoadingSpinner size="sm" className="mx-auto" />
 *
 * // Large spinner with custom styling
 * <LoadingSpinner size="lg" className="my-8" />
 * ```
 */
export function LoadingSpinner({ size = "md", centered = false, className = "" }: LoadingSpinnerProps): ReactElement {
  const spinner = (
    <Loader2 size={sizeConfig[size]} className={cn("animate-spin text-lumi-accent", className)} aria-label="Loading" />
  );

  if (centered) {
    return <div className="min-h-screen bg-black flex items-center justify-center">{spinner}</div>;
  }

  return spinner;
}
